import React, { useEffect } from "react";
import { IonSelectOption } from "@ionic/react";
import { getCustomerFullAddress } from "../../../utils/utils";
import CommonFormElement from "../../UI/CommonFormElements/CommonFormElement";
import Select from "../../Select/Select";

import classNames from "classnames/bind";
import styles from "../ShippingAddressComponent.module.scss";
import { useCustomerAddressesStateData } from "../../../providers/CustomerAddresses/CustomerAddressesStateContext";
import { useAuth } from "../../../contexts/auth-context";
import {
  areTwoAddressesSimilar,
  AddressInputWithId,
  useCustomerAddressesStorageData,
} from "../../../providers/customerAddressesStorage";
import { useConfig } from "../../../providers/config";
import { useMarketMode } from "../../../providers/marketMode";
import { useHistory } from "react-router";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { Address } from "@core/types";
import { useGetCustomerAddresses } from "../../../providers/CustomerAddresses";
import { useTranslation } from "react-i18next";

interface ICustomerAddressInput {
  setUnAuthCustomerAddress?: (address: AddressInputWithId) => void;
  onSelectChange: (address: Address) => void;
  invalidText?: string;
  selectDisabled: boolean;
}

const cx = classNames.bind(styles);

const CustomerAddressInput: React.FC<ICustomerAddressInput> = ({
  invalidText,
  selectDisabled,
  onSelectChange,
  setUnAuthCustomerAddress,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isPathnameWithCondition =
    history.location.pathname.includes("shipping") ||
    history.location.pathname.includes("order") ||
    history.location.pathname.includes("cart");

  const config = useConfig();
  const { isUserAuthenticated } = useAuth();
  const { unAuthedCustomerAddresses, currentShippingAddressId } = useCustomerAddressesStorageData();
  const { setIsAddressModalOpen } = useCustomerAddressesStateData();
  const { mode } = useMarketMode();

  const { activeShippingZone } = useActiveOrMainShippingZone();

  const { user } = useAuth();

  const { data: customerAddresses, error: customerAddressesError } = useGetCustomerAddresses({
    customerId: user?.id || "",
    options: {
      enabled: !!user?.id,
    },
  });

  useEffect(() => {
    if (customerAddresses || (config?.isExpressDeliveryEnabled && mode === "express")) {
      const addr =
        customerAddresses?.length === 1 && !!user?.id && !customerAddressesError
          ? customerAddresses[0]
          : customerAddresses?.find((addr) => {
              const unAuthAddr = unAuthedCustomerAddresses.find((addr) => addr.id === currentShippingAddressId);
              if (!unAuthAddr) return;
              return areTwoAddressesSimilar(unAuthAddr, addr);
            });

      if (addr) {
        onSelectChange(addr);
      }
    }
  }, [
    mode,
    onSelectChange,
    customerAddresses,
    customerAddressesError,
    currentShippingAddressId,
    unAuthedCustomerAddresses,
    config?.isExpressDeliveryEnabled,
    user?.id,
  ]);

  useEffect(() => {
    if (currentShippingAddressId) {
      if (!isUserAuthenticated) {
        const address = unAuthedCustomerAddresses.find((addr) => addr.id === currentShippingAddressId);
        if (!address) return;

        if (!setUnAuthCustomerAddress) return;

        setUnAuthCustomerAddress(address);
        return;
      }

      onSelectChange(customerAddresses?.find((addr) => addr.id === currentShippingAddressId)!);
    }
  }, []);

  const filterAdresses = <T extends { city?: string | null }[]>(addrs: T): T => {
    if (isPathnameWithCondition) {
      const currentZoneName = activeShippingZone?.name ?? "";

      return addrs.filter((a) => a.city?.includes(currentZoneName)) as T;
    }

    return addrs;
  };

  return (
    <CommonFormElement
      classListItem={cx("shipping__addressSelect")}
      label={t("Delivery address")}
      isValid={!invalidText}
      errorMessage={invalidText}
    >
      <Select
        disabled={selectDisabled}
        value={currentShippingAddressId}
        isValid={!!currentShippingAddressId}
        placeholder={t("Delivery address")}
        className={"shippingAddress_deliverySelect"}
        onChange={(e: any) => {
          if (e.detail.value === "ADD_NEW") {
            history.replace("/home3");
            setIsAddressModalOpen(true);
            return;
          }

          if (!isUserAuthenticated) {
            const address = unAuthedCustomerAddresses.find((addr) => addr.id === e.detail.value);
            if (!address) return;

            if (!setUnAuthCustomerAddress) return;
            setUnAuthCustomerAddress(address);
            return;
          }

          onSelectChange(customerAddresses?.find((addr) => addr.id === e.detail.value)!);
        }}
      >
        {!isUserAuthenticated
          ? filterAdresses(unAuthedCustomerAddresses).map((address) => {
              return (
                <IonSelectOption
                  key={address.id}
                  value={address.id}
                  disabled={
                    isPathnameWithCondition && activeShippingZone && !address.city?.includes(activeShippingZone.name)
                  }
                >
                  {getCustomerFullAddress(address)}
                </IonSelectOption>
              );
            })
          : filterAdresses(customerAddresses ?? []).map((address) => {
              return (
                <IonSelectOption
                  key={address.id}
                  value={address.id}
                  disabled={
                    isPathnameWithCondition && activeShippingZone && !address.city?.includes(activeShippingZone.name)
                  }
                >
                  {getCustomerFullAddress(address)}
                </IonSelectOption>
              );
            })}
        <IonSelectOption value="ADD_NEW" className={"shippingAddress_deliverySelectOption"}>
          {t("Add new")}
        </IonSelectOption>
      </Select>
    </CommonFormElement>
  );
};

export { CustomerAddressInput };
